import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getLookupData } from 'modules/api/lookups';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';
import { getIsReseller } from 'store/publisher/selectors';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import { findSelectedRouteFrameCode } from 'utils/frameList';
import {
  ExcludedSearchOptions,
  LocalSearchOptions,
} from 'components/pages/Planner/PlannerSections/Objectives/Objectives.types';
import { RouteFrameCodeResponse } from '../FrameList.types';

export const useFrameListSearch = (
  selectedRouteFrameCodes: RouteFrameCode[],
  localSearchOptions: LocalSearchOptions = new Set(),
  excludedSearchOptions: ExcludedSearchOptions = new Set(),
): {
  fetchedOptions: RouteFrameCode[];
  searchText: string;
  setSearchText: (text: string) => void;
  openCategories: string[];
  onToggleCategory: (category: string) => void;
  isOptionIncluded: (code: string, mediaOwner?: string) => boolean | undefined;
  isOptionSelected: (code: string, mediaOwner: string) => boolean;
} => {
  const isReseller = useSelector(getIsReseller);
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);

  const [fetchedOptions, setFetchedOptions] = useState<RouteFrameCode[]>([]);
  const [searchText, setSearchText] = useState('');
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const onToggleCategory = (category: string): void => {
    if (openCategories.includes(category))
      setOpenCategories(openCategories.filter((openCategory) => openCategory !== category));
    else setOpenCategories([...openCategories, category]);
  };

  const fetchRouteFrameCodes = async (queryString: string): Promise<void> => {
    if (queryString.length < 2) return;

    const requestArguments = { cancelFunctions, lookupURLName: 'routeframecode', query: '', lookupCode: queryString };

    try {
      let response: RouteFrameCodeResponse[];

      if (isReseller) {
        response = (await multiPublisherRequest(environmentId, resellerMediaOwners, getLookupData, {
          ...requestArguments,
        })) as RouteFrameCodeResponse[];
      } else {
        response = await getLookupData(requestArguments);
      }

      const mappedData: RouteFrameCode[] = response.reduce((acc, { code, publisher }) => {
        if (!excludedSearchOptions.has(code)) {
          acc.push({
            code,
            name: code,
            mediaOwner: publisher || environmentId,
          });
        }
        return acc;
      }, [] as RouteFrameCode[]);
      setFetchedOptions(mappedData);

      if (mappedData.length > 0 && mappedData.length <= 3) setOpenCategories([...openCategories, 'Results']);
      else setOpenCategories(openCategories.filter((category) => category !== 'Results'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const searchLocalOptions = (queryString: string): void => {
    if (queryString.length < 2) return;

    const filteredOptions = Array.from(localSearchOptions).filter((option) =>
      option.name.toLowerCase().includes(queryString.toLowerCase()),
    );

    setFetchedOptions(filteredOptions);
  };

  useEffect(() => {
    if (!searchText) {
      setFetchedOptions([]);
    }

    if (localSearchOptions.size > 0) {
      searchLocalOptions(searchText);

      return;
    }

    fetchRouteFrameCodes(searchText);
  }, [searchText]);

  const isOptionIncluded = (code: string, mediaOwner?: string): boolean | undefined => {
    const foundSelectedFiles = findSelectedRouteFrameCode(selectedRouteFrameCodes, code, mediaOwner);

    if (!foundSelectedFiles) {
      return undefined;
    }

    return foundSelectedFiles?.include || false;
  };

  const isOptionSelected = (code: string, mediaOwner: string): boolean =>
    !!findSelectedRouteFrameCode(selectedRouteFrameCodes, code, mediaOwner);

  return {
    fetchedOptions,
    searchText,
    setSearchText,
    openCategories,
    onToggleCategory,
    isOptionIncluded,
    isOptionSelected,
  };
};
